import React, {
  ComponentProps,
  PropsWithChildren,
  ReactElement,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  DeepcAvatar,
  DeepcAvatarFallback,
  DeepcIcon,
  DeepcProductLogo,
  DeepcSidebar,
  DeepcSidebarContent,
  DeepcSidebarFooter,
  DeepcSidebarGroup,
  DeepcSidebarGroupLabel,
  DeepcSidebarHeader,
  DeepcSidebarInset,
  DeepcSidebarLogo,
  DeepcSidebarMenu,
  DeepcSidebarMenuButton,
  DeepcSidebarMenuItem,
  DeepcSidebarProvider,
  DeepcSidebarTrigger,
  DeepcThemeProvider,
  type DeepcIconName,
} from '@deepc/ui';
import { UserMenu } from './UserMenu';
import { AnalyticsProvider } from '@internal/backstage-plugin-reporting';
import { BILLING_FEATURE_FLAG } from '@internal/backstage-plugin-billing';
import {
  DISCOVER_FEATURE_FLAG,
  SubmitAiIcon,
} from '@internal/backstage-plugin-discover';

import './style.css';
import { getSecondLevelDomain } from './utils';
import PrivacyDialog from './TCDialog';
import { identifyUser, resetStatistics } from '../../statistics';
import {
  discoveryApiRef,
  featureFlagsApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

const domain = getSecondLevelDomain();

const isExternal = (href: string) => /^https?:\/\//.test(href);

type NavItem = {
  /** A registered icon, or custom artwork that isn't a Material Symbol. */
  icon: DeepcIconName | ReactElement;
  label: string;
  href?: string;
  onClick?: () => void;
};

type NavGroup = { label?: string; items: NavItem[] };

type SidebarLinkProps = ComponentProps<'a'> & { href: string };

/**
 * Routes in-app links through react-router so navigating doesn't reload the
 * page; absolute URLs (docs, Submit AI) stay plain anchors.
 */
const SidebarLink = forwardRef<HTMLAnchorElement, SidebarLinkProps>(
  ({ href, children, ...props }, ref) =>
    isExternal(href) ? (
      <a ref={ref} href={href} {...props}>
        {children}
      </a>
    ) : (
      <RouterLink ref={ref} to={href} {...props}>
        {children}
      </RouterLink>
    ),
);

/** An item is active on its own path and on any page nested below it. */
const isActivePath = (pathname: string, href?: string) =>
  !!href &&
  !isExternal(href) &&
  (pathname === href || pathname.startsWith(`${href}/`));

const initialsOf = (label: string) => label.charAt(0).toUpperCase();

const NavMenuItem = ({ item, active }: { item: NavItem; active: boolean }) => {
  const content = (
    <>
      {typeof item.icon === 'string' ? (
        <DeepcIcon name={item.icon} />
      ) : (
        // The menu button shrinks a direct svg child to 16px; the span keeps
        // custom artwork at the 24px of the font icons.
        <span aria-hidden className="inline-flex size-6 shrink-0">
          {item.icon}
        </span>
      )}
      <span>{item.label}</span>
    </>
  );

  return (
    <DeepcSidebarMenuItem>
      <DeepcSidebarMenuButton
        asChild={Boolean(item.href)}
        isActive={active}
        tooltip={item.label}
        onClick={item.onClick}
        aria-current={active ? 'page' : undefined}
      >
        {item.href ? (
          <SidebarLink href={item.href}>{content}</SidebarLink>
        ) : (
          content
        )}
      </DeepcSidebarMenuButton>
    </DeepcSidebarMenuItem>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const identityApi = useApi(identityApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const { pathname } = useLocation();
  const muiTheme = useTheme();

  useEffect(() => {
    const fetchUserIdentity = async () => {
      try {
        const [profile, backstageIdentity] = await Promise.all([
          identityApi.getProfileInfo(),
          identityApi.getBackstageIdentity(),
        ]);
        if (profile.email) {
          setUserEmail(profile.email);
        } else if (profile.displayName) {
          setUserName(profile.displayName);
        }

        // Identify the user to PostHog so sessions stitch across devices and
        // retention is measured per person. The userEntityRef is an opaque
        // UUID here; we send only a boolean and the email domain, never the
        // raw email. See statistics.ts for the reasoning.
        const emailDomain = profile.email?.split('@')[1]?.toLowerCase();
        identifyUser(backstageIdentity.userEntityRef, {
          is_internal: emailDomain === 'deepc.ai',
          email_domain: emailDomain,
        });
      } catch (error) {
        console.log('[UserAvatar] Failed to fetch identity:', error);
      }
    };
    fetchUserIdentity();
  }, [identityApi]);

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  let client_id = '6c4phg0ag6dirlcoerhtmlh1r3'; // default: deepc-engineering.de

  if (window.location.host.includes('deepc.pro')) {
    client_id = '64ol4acugnrqm49ptg0n2iommp';
  } else if (window.location.host.includes('deepc-stc.com')) {
    client_id = '38ko3fhdv9s3th9m1linqmp6bi';
  } else if (window.location.host.includes('deepc-services.com')) {
    client_id = '6qcadbiqmj617kfgnddlahtm9m';
  } else if (window.location.host.includes('deepc-ai.co.uk')) {
    client_id = '5a503o9uvill7dcn0juslsp8u3';
  }

  const logout_link = `https://osportal-deepc-engineering.auth.eu-central-1.amazoncognito.com/logout?client_id=${client_id}&logout_uri=https%3A%2F%2F${window.location.host}%2Foauth2%2Fsign_out`;

  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const handleLogout = async () => {
    // Clear the PostHog identity before signing out so the next person on a
    // shared workstation doesn't inherit this user's identity.
    resetStatistics();
    try {
      const blackpearl_base_url = await discoveryApi.getBaseUrl('blackpearl');
      const response = await fetch(
        `${blackpearl_base_url}/radpairConnectionData`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      const { apiUrl } = await response.json();
      await fetch(`${apiUrl}/users/sign_out?revoke=true`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error revoking Radpair token:', error);
    } finally {
      window.location.href = logout_link;
    }
  };

  const showBilling = featureFlagsApi.isActive(BILLING_FEATURE_FLAG);
  const showDiscover = featureFlagsApi.isActive(DISCOVER_FEATURE_FLAG);

  const allGroups: NavGroup[] = [
    {
      items: showDiscover
        ? [{ icon: 'explore', label: 'Discover', href: '/discover' }]
        : [],
    },
    {
      label: 'Govern',
      items: [
        { icon: 'table_chart_view', label: 'Analytics', href: '/analytics' },
        { icon: 'dns', label: 'System Manager', href: '/fleet' },
        ...(showBilling
          ? [
              {
                icon: 'credit_card',
                label: 'Billing',
                href: '/billing',
              } as const,
            ]
          : []),
      ],
    },
    {
      label: 'Deploy',
      items: [
        { icon: 'category', label: 'AI Portfolio', href: '/portfolio' },
        { icon: 'assignment', label: 'AI Reporting', href: '/reporting' },
      ],
    },
    {
      label: 'Build',
      items: [
        {
          icon: <SubmitAiIcon style={{ fontSize: 24 }} />,
          label: 'Submit AI',
          href: `https://build.one.${domain}/`,
        },
        {
          icon: 'code_blocks',
          label: 'Developer Docs',
          href: `https://docs.one.${domain}/`,
        },
      ],
    },
  ];
  const groups = allGroups.filter(group => group.items.length > 0);

  const userLabel = userEmail ? userEmail.split('@')[0] : userName || '';

  // Without a resolved identity there is no user card to open the menu from,
  // so sign-out is offered directly, as before.
  const footerItems: NavItem[] = [
    { icon: 'contact_support', label: 'Support', href: '/support' },
    ...(userLabel
      ? []
      : [{ icon: 'lock', label: 'Logout', onClick: handleLogout } as const]),
  ];

  // Match the library's primary to the active Backstage theme, as the engine
  // comparison page does, so the sidebar highlight uses the host's accent.
  const primary = useMemo(
    () => ({
      ...muiTheme.palette.primary,
      contrastText: muiTheme.palette.getContrastText(
        muiTheme.palette.primary.main,
      ),
    }),
    [muiTheme],
  );

  return (
    <DeepcThemeProvider primary={primary}>
      <PrivacyDialog />
      <DeepcSidebarProvider collapseBelow={1024}>
        <DeepcSidebar collapsible="icon">
          <DeepcSidebarHeader>
            <DeepcSidebarLogo
              logo={
                <SidebarLink href="/portfolio" aria-label="Home">
                  <DeepcProductLogo
                    product="hub"
                    height={26}
                    aria-label="deepcOS Hub"
                  />
                </SidebarLink>
              }
            />
            <DeepcSidebarTrigger />
          </DeepcSidebarHeader>
          <DeepcSidebarContent>
            <nav aria-label="Main" className="flex flex-col gap-2">
              {groups.map((group, index) => (
                <DeepcSidebarGroup key={group.label ?? index}>
                  {group.label ? (
                    <DeepcSidebarGroupLabel>
                      {group.label}
                    </DeepcSidebarGroupLabel>
                  ) : null}
                  <DeepcSidebarMenu>
                    {group.items.map(item => (
                      <NavMenuItem
                        key={item.label}
                        item={item}
                        active={isActivePath(pathname, item.href)}
                      />
                    ))}
                  </DeepcSidebarMenu>
                </DeepcSidebarGroup>
              ))}
            </nav>
          </DeepcSidebarContent>
          <DeepcSidebarFooter>
            <DeepcSidebarMenu>
              {footerItems.map(item => (
                <NavMenuItem
                  key={item.label}
                  item={item}
                  active={isActivePath(pathname, item.href)}
                />
              ))}
              {userLabel ? (
                <DeepcSidebarMenuItem>
                  <DeepcSidebarMenuButton
                    size="lg"
                    tooltip={userLabel}
                    onClick={event => setAnchorEl(event.currentTarget)}
                  >
                    <DeepcAvatar size="sm">
                      <DeepcAvatarFallback>
                        {initialsOf(userLabel)}
                      </DeepcAvatarFallback>
                    </DeepcAvatar>
                    <div className="flex flex-col text-left leading-tight">
                      <span className="text-sm font-medium">{userLabel}</span>
                      {userEmail ? (
                        <span className="text-xs text-muted-foreground">
                          {userEmail}
                        </span>
                      ) : null}
                    </div>
                  </DeepcSidebarMenuButton>
                </DeepcSidebarMenuItem>
              ) : null}
            </DeepcSidebarMenu>
          </DeepcSidebarFooter>
        </DeepcSidebar>
        <DeepcSidebarInset>
          <AnalyticsProvider>{children}</AnalyticsProvider>
        </DeepcSidebarInset>
      </DeepcSidebarProvider>
      <UserMenu
        anchorEl={anchorEl}
        userLabel={userEmail || userName || ''}
        onClose={handleUserMenuClose}
        onLogout={handleLogout}
      />
    </DeepcThemeProvider>
  );
};
